import { useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { formatPot } from './format';
import { PlayerResultRow } from './PlayerResultRow';
import { GameType, PotGroupSnapshot } from './types';

import styles from './HandHistory.module.scss';

export const PotGroup = ({
  pot,
  gameType,
  viewerSeat,
  avatarBySeat,
  boardOf,
  showAmount = true,
  collapsible = false,
  defaultOpen = true
}: {
  pot: PotGroupSnapshot;
  gameType: GameType;
  viewerSeat?: number;
  avatarBySeat?: Record<number, string | undefined>;
  /** The board that paid a row: its run's on a multi-run hand. */
  boardOf?: (run?: number) => string[] | undefined;
  showAmount?: boolean;
  collapsible?: boolean;
  defaultOpen?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  // Run by run, Run 1 → 2 → 3 (a single board has one); within a run, winners
  // first, and among them (and among the rest) the viewer's own row leads, so
  // it needs no hunting for. Otherwise the record's order stands.
  const isViewerRow = (seat: number) => viewerSeat !== undefined && seat === viewerSeat;
  const sortedPlayers = pot.players
    .map((player, index) => ({ player, index }))
    .sort((a, b) => {
      if ((a.player.run ?? 0) !== (b.player.run ?? 0)) {
        return (a.player.run ?? 0) - (b.player.run ?? 0);
      }
      if (a.player.isWinner !== b.player.isWinner) {
        return a.player.isWinner ? -1 : 1;
      }
      if (isViewerRow(a.player.seat) !== isViewerRow(b.player.seat)) {
        return isViewerRow(a.player.seat) ? -1 : 1;
      }

      return a.index - b.index;
    })
    .map(({ player }) => player);

  return (
    <section className={styles.potGroup}>
      <button
        type='button'
        className={`${styles.potHeader} ${collapsible ? styles.potHeaderButton : ''}`}
        disabled={!collapsible}
        aria-expanded={collapsible ? isOpen : undefined}
        onClick={() => collapsible && setIsOpen((current) => !current)}>
        <span>{pot.name}</span>
        <span className={styles.potHeaderMeta}>
          {showAmount && <strong>{formatPot(pot.amount, gameType)}</strong>}
          {collapsible && (
            <KeyboardArrowDownIcon className={isOpen ? styles.toggleOpen : undefined} />
          )}
        </span>
      </button>
      {isOpen && (
        <div className={styles.playerRows}>
          {sortedPlayers.map((player) => (
            <PlayerResultRow
              key={`${pot.name}-${player.playerId}-${player.seat}-${player.run ?? ''}`}
              player={player}
              gameType={gameType}
              isViewer={player.seat === viewerSeat}
              fallbackAvatar={avatarBySeat?.[player.seat]}
              board={boardOf?.(player.run)}
            />
          ))}
        </div>
      )}
    </section>
  );
};
